import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { useBoolean } from 'ahooks';
import { Dropdown, Tooltip } from 'antd';
import { isObject } from 'lodash';
import React, {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  CreateBranchingDetailsProps,
  FormChoiceOption,
  FormQuestion,
  FormQuestionType,
} from 'api/form/types';
import { ReactComponent as AddQuestionAboveSVG } from 'assets/svgs/add-question-above.svg';
import { ReactComponent as AddQuestionBelowSVG } from 'assets/svgs/add-question-below.svg';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { computeFieldRenderer } from 'components/organisms/forms';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate } from 'hooks/use-translation';
import {
  useCreateFieldQuestionMutation,
  useDeleteFieldQuestionMutation,
  useDeleteOptionBranchingMutation,
} from 'queries/form-template';
import { getTextFromHTML } from 'utils/getTextFromHTML';
import {
  FormQuestionLoading,
  FormQuestionSelect,
  ItemSelectionWrapper,
} from './form-builder-components';
import { DefaultFormQuestionValues } from './utils';

const FORM_QUESTION_ACTIONS_HEIGHT = 120;

interface FormQuestionRendererProps {
  formQuestion: FormQuestion;
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
  templateId: number;
  selectedFormItem: number | FormQuestion | null;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
  createBranchingDetails: null | CreateBranchingDetailsProps;
  setCreateBranchingDetails: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
}

interface OptionQuestionsRendererProps {
  option: FormChoiceOption;
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
  templateId: number;
  selectedFormItem: number | FormQuestion | null;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
  createBranchingDetails: null | CreateBranchingDetailsProps;
  setCreateBranchingDetails: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
}

interface DuplicateFormQuestionProps
  extends Omit<DefaultFormQuestionValues, 'rows'> {
  rows?: FormQuestion[];
}

const Wrapper = styled.div`
  flex-grow: 1;

  .section {
    pointer-events: auto !important;
  }
`;
const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  margin: var(--spacing-05) 0;
`;
const OptionHeaderContent = styled.div`
  width: 100%;
  padding: var(--spacing-02) var(--spacing-05);
  background: var(--white-100);
  border-radius: 2px;
  border: 1px solid var(--coolest-gray-10);
  display: flex;
  align-items: center;
  color: var(--gray-80);
  gap: var(--spacing-03);
`;
const OptionLabelButton = styled(Button)`
  color: var(--blue-60);
  margin: 0 var(--spacing-06) 0 var(--spacing-03);
  padding: var(--spacing-01) var(--spacing-03);
  flex: 1;
  overflow: auto;
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const OptionHeaderMessage = styled.span`
  white-space: nowrap;
`;
const BranchingWrapper = styled.div`
  display: flex;
`;
const BranchingLine = styled.div`
  border-left: 1px solid var(--gray-30);
  padding-right: var(--spacing-04);
  margin-left: var(--spacing-07);
`;

const FormItemActions = styled.div<{ topValue: number }>`
  position: absolute;
  padding: var(--spacing-02);
  background: var(--white-100);
  right: -20px;
  flex-direction: column;
  top: ${(props) => `${props.topValue}px`};
  height: ${FORM_QUESTION_ACTIONS_HEIGHT}px;
  display: none;
  box-shadow: var(--shadow-16);
  justify-content: center;
`;
const ItemActionButtom = styled(Button)`
  &:hover {
    color: var(--blue-60);
  }
`;
const DeleteQuestionButton = styled(Button)`
  &:hover {
    color: var(--error-color);
  }
`;

const FormItemActionsWrapper = styled.div`
  position: relative;
  margin-top: var(--spacing-05);
  /* to achieve hover effect for question more actions. */
  margin-right: -30px;
  padding-right: 30px;

  &:hover ${FormItemActions} {
    display: flex;
  }
  section {
    padding: var(--spacing-05);
    background: var(--white-100);
  }
  &:hover ${ItemSelectionWrapper} {
    border-color: var(--blue-60);
    box-shadow: var(--shadow-16);
    opacity: 1;
  }
`;

interface CreateQuestionBranchingProps {
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
  setCreateBranchingDetails: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
  selectedOption: FormChoiceOption;
  templateId: number;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
}
const CreateQuestionBranching: FC<CreateQuestionBranchingProps> = ({
  setCreateBranchingDetails,
  selectedOption,
  templateId,
  setSelectedFormItem,
  centerColWrapperRef,
}) => {
  return (
    <>
      <OptionHeader>
        <OptionHeaderContent>
          <Button size="small" type="text" icon={<ChevronDown16 />} />
          <OptionHeaderMessage>
            <FormattedMessage id="form_templates.option.option_value_header" />
          </OptionHeaderMessage>
          <OptionLabelButton type="text" size="small" disabled>
            {selectedOption.optionLabel}
          </OptionLabelButton>
        </OptionHeaderContent>
      </OptionHeader>
      <OutsideClickHandler
        onOutsideClick={() => setCreateBranchingDetails(null)}
      >
        <BranchingWrapper>
          <BranchingLine />
          <FormQuestionSelect
            centerColWrapperRef={centerColWrapperRef}
            templateId={templateId}
            position={0}
            branchingFrom={selectedOption.optionId}
            onQuestionSelectSuccess={() => setCreateBranchingDetails(null)}
            setSelectedFormItem={setSelectedFormItem}
          />
        </BranchingWrapper>
      </OutsideClickHandler>
    </>
  );
};

export const FormQuestionRenderer: FC<FormQuestionRendererProps> = ({
  formQuestion,
  centerColWrapperRef,
  templateId,
  selectedFormItem,
  setSelectedFormItem,
  createBranchingDetails,
  setCreateBranchingDetails,
}) => {
  const translate = useTranslate();
  const { control } = useForm();
  const [addingQuestionDirection, setAddingQuestionDirection] =
    useState<'above' | 'below' | null>(null);

  const [copyingQuestion, setCopyingQuestion] = useState(false);

  const questionWrapperRef = useRef<HTMLDivElement>(null);
  const FieldRenderer = computeFieldRenderer(formQuestion.formQuestionType);

  const { mutate: createQuestionMutation, isLoading: isCreateQuestionLoading } =
    useCreateFieldQuestionMutation(templateId);

  const { mutateAsync: deleteQuestionMutation } =
    useDeleteFieldQuestionMutation({
      templateId,
      questionText: formQuestion.questionText,
    });

  const onQuestionDeleteOk = async () => {
    await deleteQuestionMutation({ questionId: formQuestion.questionId });
    if (
      isObject(selectedFormItem) &&
      selectedFormItem?.questionId === formQuestion.questionId
    ) {
      setSelectedFormItem(null);
    }
  };
  const deleteQuestionConfirmation = useDeleteConfirmModal({
    title: translate('form_templates.question.delete.title'),
    content: translate('form_templates.question.delete.message', {
      questionText: getTextFromHTML(formQuestion.questionText),
      strong: (chunks) => <strong>{chunks}</strong>,
    }),
    onOk: async () => {
      await onQuestionDeleteOk();
    },
  });

  const deleteQuestionWithBranchConfirmation = useDeleteConfirmModal({
    title: translate('form_templates.question.delete.title'),
    content: translate('form_templates.question_with_branch.delete.message', {
      questionText: getTextFromHTML(formQuestion.questionText),
      strong: (chunks) => <strong>{chunks}</strong>,
    }),
    onOk: async () => {
      await onQuestionDeleteOk();
    },
  });

  const createDuplicateQuestion = () => {
    const formQuestionValues = {
      questionText: formQuestion.questionText,
      description: formQuestion.description,
      options: (formQuestion.options || []).map((option) => {
        return {
          optionLabel: option.optionLabel,
          position: option.position,
          color: option.color,
        };
      }),
      formQuestionType: formQuestion.formQuestionType,
      position: formQuestion.position + 1,
      templateId,
      required: formQuestion.required,
    } as DuplicateFormQuestionProps;

    if (formQuestion.rows) {
      formQuestionValues.rows = formQuestion.rows;
    }

    if (formQuestion.branchingFrom) {
      formQuestionValues.branchingFrom = formQuestion.branchingFrom;
    }
    if (formQuestion.questionMeta) {
      formQuestionValues.questionMeta = formQuestion.questionMeta;
    }
    setCopyingQuestion(true);
    createQuestionMutation(formQuestionValues, {
      onSuccess: (response) => {
        setCopyingQuestion(false);
        setSelectedFormItem(response);
      },
    });
  };

  const deleteQuestion = () => {
    if (formQuestion.options) {
      const hasbranches = formQuestion.options.some((question) => {
        return question.branches && question.branches.length > 0;
      });

      if (hasbranches) {
        return deleteQuestionWithBranchConfirmation();
      }
    }

    return deleteQuestionConfirmation();
  };

  return FieldRenderer ? (
    <Wrapper>
      {addingQuestionDirection === 'above' && (
        <OutsideClickHandler
          onOutsideClick={() => setAddingQuestionDirection(null)}
        >
          <FormQuestionSelect
            centerColWrapperRef={centerColWrapperRef}
            templateId={templateId}
            branchingFrom={
              formQuestion.branchingFrom
                ? formQuestion.branchingFrom
                : undefined
            }
            position={formQuestion.position}
            onQuestionSelectSuccess={() => setAddingQuestionDirection(null)}
            setSelectedFormItem={setSelectedFormItem}
          />
        </OutsideClickHandler>
      )}
      <FormItemActionsWrapper ref={questionWrapperRef}>
        <ItemSelectionWrapper
          isSelected={
            selectedFormItem && isObject(selectedFormItem)
              ? (selectedFormItem as FormQuestion).questionId ===
                formQuestion.questionId
              : false
          }
          onClick={() => {
            /**
             * passing new reference of selected item on click
             * to focus field configuration component.
             */
            setSelectedFormItem({ ...formQuestion });
          }}
        >
          <section>
            <FieldRenderer field={formQuestion} control={control} disabled />
          </section>
        </ItemSelectionWrapper>
        {/* Position items at the center of form question */}
        <FormItemActions
          topValue={
            questionWrapperRef && questionWrapperRef.current
              ? [
                  FormQuestionType.CheckBoxGrid,
                  FormQuestionType.MultipleChoiceGrid,
                ].includes(formQuestion.formQuestionType)
                ? 0
                : questionWrapperRef.current.clientHeight / 2 -
                  FORM_QUESTION_ACTIONS_HEIGHT / 2
              : 0
          }
        >
          <Tooltip
            mouseEnterDelay={0.8}
            placement="right"
            title={
              <FormattedMessage id="form_templates.actions.add_question_above" />
            }
          >
            <ItemActionButtom
              size="small"
              type="text"
              icon={<AddQuestionAboveSVG />}
              onClick={() => setAddingQuestionDirection('above')}
            />
          </Tooltip>

          <Tooltip
            mouseEnterDelay={0.8}
            placement="right"
            title={
              <FormattedMessage id="form_templates.actions.add_question_below" />
            }
          >
            <ItemActionButtom
              size="small"
              type="text"
              icon={<AddQuestionBelowSVG />}
              onClick={() => setAddingQuestionDirection('below')}
            />
          </Tooltip>

          {!formQuestion.projectFieldId && !formQuestion.taskFieldId ? (
            <Tooltip
              mouseEnterDelay={0.8}
              placement="right"
              title={<FormattedMessage id="duplicate" />}
            >
              <ItemActionButtom
                size="small"
                type="text"
                icon={<Copy16 />}
                onClick={createDuplicateQuestion}
              />
            </Tooltip>
          ) : null}

          <Tooltip
            mouseEnterDelay={0.8}
            placement="right"
            title={<FormattedMessage id="delete" />}
          >
            <DeleteQuestionButton
              size="small"
              type="text"
              icon={<TrashCan16 />}
              onClick={deleteQuestion}
            />
          </Tooltip>
        </FormItemActions>
      </FormItemActionsWrapper>

      {formQuestion.options?.map((option) => {
        if (
          createBranchingDetails &&
          createBranchingDetails?.questionId === formQuestion.questionId &&
          createBranchingDetails.optionId === option.optionId
        ) {
          return (
            <CreateQuestionBranching
              centerColWrapperRef={centerColWrapperRef}
              selectedOption={option}
              setCreateBranchingDetails={setCreateBranchingDetails}
              templateId={templateId}
              setSelectedFormItem={setSelectedFormItem}
            />
          );
        }

        return option.branches?.length ? (
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          <OptionQuestionsRenderer
            centerColWrapperRef={centerColWrapperRef}
            option={option}
            templateId={templateId}
            selectedFormItem={selectedFormItem}
            setSelectedFormItem={setSelectedFormItem}
            createBranchingDetails={createBranchingDetails}
            setCreateBranchingDetails={setCreateBranchingDetails}
          />
        ) : null;
      })}

      {addingQuestionDirection === 'below' && (
        <OutsideClickHandler
          onOutsideClick={() => setAddingQuestionDirection(null)}
        >
          <FormQuestionSelect
            centerColWrapperRef={centerColWrapperRef}
            templateId={templateId}
            branchingFrom={
              formQuestion.branchingFrom
                ? formQuestion.branchingFrom
                : undefined
            }
            position={formQuestion.position + 1}
            onQuestionSelectSuccess={() => setAddingQuestionDirection(null)}
            setSelectedFormItem={setSelectedFormItem}
          />
        </OutsideClickHandler>
      )}

      {copyingQuestion && isCreateQuestionLoading ? (
        <FormQuestionLoading centerColWrapperRef={centerColWrapperRef} />
      ) : null}
    </Wrapper>
  ) : null;
};

const OptionQuestionsRenderer: FC<OptionQuestionsRendererProps> = ({
  option,
  centerColWrapperRef,
  templateId,
  selectedFormItem,
  setSelectedFormItem,
  createBranchingDetails,
  setCreateBranchingDetails,
}) => {
  const [isExpanded, { setTrue: expandOptions, setFalse: collapseOptions }] =
    useBoolean(true);

  const translate = useTranslate();
  const { mutateAsync } = useDeleteOptionBranchingMutation({
    templateId,
    optionLabel: option.optionLabel,
  });

  const branchDeleteConfirmation = useDeleteConfirmModal({
    title: translate('form_templates.delete_branch_title'),
    content: translate('form_templates.delete_branch_message', {
      optionLabel: option.optionLabel,
      strong: (chunks) => <strong>{chunks}</strong>,
    }),
    onOk: () => mutateAsync({ optionId: option.optionId }),
  });

  return (
    <>
      <OptionHeader>
        <OptionHeaderContent>
          <Button
            size="small"
            type="text"
            icon={isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
            onClick={() => {
              return isExpanded ? collapseOptions() : expandOptions();
            }}
          />
          <OptionHeaderMessage>
            <FormattedMessage id="form_templates.option.option_value_header" />
          </OptionHeaderMessage>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Header>
                  <FormattedMessage id="more_actions" />
                </Menu.Header>
                <Menu.ItemDelete
                  key="form_templates.delete_branch"
                  onClick={() => {
                    branchDeleteConfirmation();
                  }}
                >
                  <FormattedMessage id="form_templates.delete_branch" />
                </Menu.ItemDelete>
              </Menu>
            }
            trigger={['click']}
            placement="bottomLeft"
          >
            <OptionLabelButton type="text" size="small">
              {option.optionLabel}
            </OptionLabelButton>
          </Dropdown>
        </OptionHeaderContent>
      </OptionHeader>
      {isExpanded
        ? (option.branches ?? []).map((branchQuestion) => {
            return (
              <BranchingWrapper>
                <BranchingLine />
                <FormQuestionRenderer
                  key={branchQuestion.questionId}
                  centerColWrapperRef={centerColWrapperRef}
                  formQuestion={branchQuestion}
                  templateId={templateId}
                  selectedFormItem={selectedFormItem}
                  setSelectedFormItem={setSelectedFormItem}
                  createBranchingDetails={createBranchingDetails}
                  setCreateBranchingDetails={setCreateBranchingDetails}
                />
              </BranchingWrapper>
            );
          })
        : null}
    </>
  );
};
